;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import { __assign, __rest as __rest_1, __spreadArrays } from "tslib"
import { __rest } from "tslib"
import type { FieldMergeFunction, FieldReadFunction } from "@apollo/client/cache"

// Most of this file was taken from the source code. It was updated to enable pagintation rather
// than displaying all of the results form the cache
// https://www.apollographql.com/docs/react/pagination/cursor-based/#relay-style-cursor-pagination

// Helper function to merge objects deeply
function mergeDeep(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
) {
  const result = { ...target }
  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      result[key] = mergeDeep(
        (result[key] as Record<string, unknown>) ?? {},
        source[key] as Record<string, unknown>,
      )
    } else {
      result[key] = source[key]
    }
  }
  return result
}

export const read: FieldReadFunction<any> = function (existing, _a) {
  var canRead = _a.canRead,
    readField = _a.readField
  if (!existing) return

  var edges = []
  var startCursor = existing.pageInfo.startCursor
  var endCursor = existing.pageInfo.endCursor

  let readEdges = false

  // iterate over edeges from back.
  // if record edges, record.
  // if start cursor is on edge. stop recording
  // if edge is end cursor
  // check that edge before also isn't end cursor
  // if true, record edge and set variable to read other edges
  let i
  for (i = existing.edges.length - 1; i >= 0; i--) {
    let edge = existing.edges[i]

    if (canRead(readField("node", edge))) {
      // if readEdges, add edge to read.
      if (readEdges) {
        // put at front of array bc we are reading backwards.
        edges.unshift(edge)
        // if start cursor is on edge. stop recording
        if (startCursor == edge.cursor) {
          readEdges = false
        }
      }
      if (edge.cursor == endCursor) {
        const edgeBefore = existing.edges[i - 1]
        // if edge before isnt end cursor, then start recording
        if (edgeBefore.cursor != endCursor) {
          edges.push(edge)
          readEdges = true
        }
      }
    }
  }

  return __assign(__assign({}, getExtras(existing)), {
    edges: edges,
    pageInfo: __assign(__assign({}, existing.pageInfo), {
      startCursor: startCursor,
      endCursor: endCursor,
    }),
  })
}

export const merge: FieldMergeFunction<any> = function (existing, incoming, _a) {
  if (existing === void 0) {
    existing = makeEmptyData()
  }
  var args = _a.args,
    isReference = _a.isReference,
    readField = _a.readField
  var incomingEdges = incoming.edges
    ? incoming.edges.map(function (edge) {
        const edgeCopy: Record<string, any> = __assign({}, edge)
        if (isReference(edgeCopy)) {
          // Object.assign instead of `edgeCopy.cursor = ...`: the isReference()
          // guard narrows edgeCopy to `Reference`, which has no `cursor` field.
          Object.assign(edgeCopy, { cursor: readField("cursor", edgeCopy) })
        }
        return edgeCopy
      })
    : []
  if (incoming.pageInfo) {
    var pageInfo_1 = incoming.pageInfo
    var startCursor = pageInfo_1.startCursor,
      endCursor = pageInfo_1.endCursor
    var firstEdge = incomingEdges[0]
    var lastEdge = incomingEdges[incomingEdges.length - 1]
    if (firstEdge && startCursor) {
      firstEdge.cursor = startCursor
    }
    if (lastEdge && endCursor) {
      lastEdge.cursor = endCursor
    }
    var firstCursor = firstEdge && firstEdge.cursor
    if (firstCursor && !startCursor) {
      incoming = mergeDeep(incoming, {
        pageInfo: {
          startCursor: firstCursor,
        },
      })
    }
    var lastCursor = lastEdge && lastEdge.cursor
    if (lastCursor && !endCursor) {
      incoming = mergeDeep(incoming, {
        pageInfo: {
          endCursor: lastCursor,
        },
      })
    }
  }
  var prefix = existing.edges
  var suffix = []
  if (args && args.after) {
    var index = prefix.findIndex(function (edge) {
      return edge.cursor === args.after
    })
    if (index >= 0) {
      prefix = prefix.slice(0, index + 1)
    }
  } else if (args && args.before) {
    var index = prefix.findIndex(function (edge) {
      return edge.cursor === args.before
    })
    suffix = index < 0 ? prefix : prefix.slice(index)
    prefix = []
  } else if (incoming.edges) {
    prefix = []
  }
  var edges = __spreadArrays(prefix, incomingEdges, suffix)
  var pageInfo = __assign(__assign({}, incoming.pageInfo), existing.pageInfo)

  if (incoming.pageInfo) {
    var _b = incoming.pageInfo,
      hasPreviousPage = _b.hasPreviousPage,
      hasNextPage = _b.hasNextPage,
      startCursor = _b.startCursor,
      endCursor = _b.endCursor,
      extras = __rest_1(_b, [
        "hasPreviousPage",
        "hasNextPage",
        "startCursor",
        "endCursor",
      ])
    Object.assign(pageInfo, extras)
    if (!prefix.length) {
      if (void 0 !== hasPreviousPage) pageInfo.hasPreviousPage = hasPreviousPage
      if (void 0 !== startCursor) pageInfo.startCursor = startCursor
    }
    if (!suffix.length) {
      if (void 0 !== hasNextPage) pageInfo.hasNextPage = hasNextPage
      if (void 0 !== endCursor) pageInfo.endCursor = endCursor
    }
  }
  return __assign(__assign(__assign({}, getExtras(existing)), getExtras(incoming)), {
    edges: edges,
    pageInfo: Object.assign({}, incoming.pageInfo),
  })
}

function offsetFromCursor(
  items: unknown[],
  cursor: string,
  readField: (fieldName: string, value: unknown) => unknown,
) {
  // Search from the back of the list because the cursor we're
  // looking for is typically the ID of the last item.
  for (let i = items.length - 1; i >= 0; --i) {
    const item = items[i]
    // Using readField works for both non-normalized objects
    // (returning item.id) and normalized references (returning
    // the id field from the referenced entity object), so it's
    // a good idea to use readField when you're not sure what
    // kind of elements you're dealing with.
    if (readField("id", item) === cursor) {
      // Add one because the cursor identifies the item just
      // before the first item in the page we care about.
      return i + 1
    }
  }
  // Report that the cursor could not be found.
  return -1
}

function makeEmptyData() {
  return {
    edges: [],
    pageInfo: {
      hasPreviousPage: false,
      hasNextPage: true,
      startCursor: "",
      endCursor: "",
    },
  }
}
var getExtras = function (obj: Record<string, unknown>) {
  return __rest(obj, notExtras)
}
var notExtras = ["edges", "pageInfo"]
